import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles3() {
  const { lesson, course, accent } = findLesson("the-beatles", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The surprise" title="Is it really just E major?">
        <Prose>
          <p>
            Ask anyone what key "With a Little Help from My Friends" is in and they'll say E
            major. Jason's analysis disagrees — carefully. The verse is indeed E major. But the
            chorus, and most of the song's memorable sections, are in <strong>E Mixolydian</strong>.
            More of this song is Mixolydian than it is major. And the two feel completely different.
          </p>
          <p>
            This is not a technicality. The distinction explains why the song has such warmth —
            why the chorus feels like a friendly lift rather than a triumphant arrival. Mixolydian
            sounds warmer and less resolved than major. It leans slightly sideways rather than
            pointing straight up. And the Beatles built an entire chorus on it.
          </p>
          <p>
            This lesson covers the verse (E major, straightforward 2-5-1), the chorus (E
            Mixolydian, with the ♭VII chord), and what to do with both hands when you play it
            solo. Part 2 builds the left hand (ghost notes, open and closed voicings, the three
            zones of the keyboard) and the right-hand ornaments.
          </p>
        </Prose>

        <TheoryBox label="Quick stats">
          <strong>Key:</strong> E major / E Mixolydian ·{" "}
          <strong>Meter:</strong> 4/4 ·{" "}
          <strong>Album:</strong> Sgt. Pepper's Lonely Hearts Club Band (1967) ·{" "}
          <strong>Singer:</strong> Ringo Starr
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="0C58ttB2-Qg"
        title="With a Little Help from My Friends — The Beatles"
        caption="Count the chord changes. Verse: E, B, F♯m and B7, all from E major. Chorus: the D major chord arrives — and the whole mood shifts. That D is the ♭VII of E Mixolydian."
      />

      <Chapter eyebrow="The verse" title="E major — a clean 2-5-1">
        <Prose>
          <p>
            The verse of "With a Little Help from My Friends" is traditional E major harmony —
            a textbook 2-5-1 sequence. Jason calls this "very traditional diatonic." The chords
            move predictably through F♯m (ii), B (V), and E (I). The Beatles are playing it
            completely straight. No borrowed chords, no surprises. Yet it sounds good.
          </p>
          <p>
            The reason: the chord tones support the melody at exactly the right moments. The
            Beatles were very particular about their dominant 7th chords, and the B7 in the verse
            is voiced carefully — it creates just enough tension to make the resolution to E feel
            earned. Getting the inversions right here matters more than it might seem.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Verse — diatonic E major, traditional 2-5-1"
          caption="Clean diatonic harmony, as on Jason's chord chart: E → B → F♯m → B7 → E, played twice. It ends with F♯m (ii) → B7 (V7) → E (I). The verse doesn't try to surprise — it creates the stable ground the chorus will then lift away from."
          chords={[
            { chord: "E", rn: "I", function: "Tonic" },
            { chord: "B", rn: "V", function: "Dominant" },
            { chord: "F#m", rn: "ii", function: "Pre-dominant" },
            { chord: "B7", rn: "V7", function: "Dominant" },
            { chord: "E", rn: "I", function: "Tonic" },
          ]}
        />
      </Chapter>

      <Chapter eyebrow="The chorus" title="E Mixolydian — the ♭VII chord changes everything">
        <Prose>
          <p>
            The chorus begins and a D major chord appears. D natural — not D♯. That D major
            chord has no place in the E major scale. E major's seventh degree is D♯ diminished.
            Yet D major sounds completely right here. More than right — it sounds like the whole
            point of the song.
          </p>
          <p>
            It comes from <strong>E Mixolydian</strong>. The Mixolydian mode is like a major
            scale with one note lowered: the seventh. E major has D♯; E Mixolydian has D♮.
            That one note change flattens the VII chord from a diminished to a plain major —
            from something angular and tense to something warm and open.
          </p>
          <p>
            Jason's observation: "Someone asks you a simple question. What scale is With a Little
            Help from My Friends? What are you going to say? It's very tough to answer that
            correctly." The answer he gives is that the key is E, but the song has extensive
            modal movement. And by movement, he means the chorus is Mixolydian, the bridge
            touches relative minor, and only a few sections are pure major. "The only part of
            the song which is major is actually this part" — the verse.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Chorus — E Mixolydian with the ♭VII lift"
          caption="D major (♭VII) arrives and lifts the harmony away from E major's gravity. The chorus feels friendly, open, reaching — not because of a dramatic modulation, but because of one borrowed note."
          chords={[
            { chord: "D", rn: "♭VII", function: "Borrowed" },
            { chord: "A", rn: "IV", function: "Pre-dominant" },
            { chord: "E", rn: "I", function: "Tonic" },
            { chord: "D", rn: "♭VII", function: "Borrowed" },
            { chord: "A", rn: "IV", function: "Pre-dominant" },
            { chord: "E", rn: "I", function: "Tonic" },
            { chord: "A", rn: "IV", function: "Pre-dominant" },
            { chord: "E", rn: "I", function: "Tonic" },
          ]}
        />

        <TheoryBox label="E Mixolydian vs. E major">
          E major scale: E, F♯, G♯, A, B, C♯, D♯. ·{" "}
          E Mixolydian scale: E, F♯, G♯, A, B, C♯, <strong>D♮</strong>. ·{" "}
          One note. The D♯ becomes D♮. That change turns the VII chord from D♯ diminished (tense,
          angular) into D major (warm, resolved-but-open). The ♭VII chord is the signature sound
          of rock and folk music — hear it in "Sweet Home Alabama" (C in D major) and in the long
          coda of "Hey Jude" (E♭ in F major).
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Playing it" title="Emulate the whole band — it's just you now">
        <Prose>
          <p>
            When you play "With a Little Help from My Friends" solo at the piano, Jason's
            reminder applies: "Remember — you are trying to emulate the whole band. There is
            only you right now." The drummer is gone. The bassist is gone. Your left hand covers
            both.
          </p>
          <p>
            The approach Jason teaches is the "working version" first — nail the essential
            elements before adding ornamentation. "In any meal, you focus on all the essential
            elements which will work, will give the person some satisfaction — but then all the
            spices should come later." For this song, the essentials are: the chord inversions
            (especially the F♯m7 with its specific voicing), the B7 dominant seventh (the Beatles
            are very particular about this), and the D major chord arriving cleanly in the chorus.
          </p>
          <p>
            The F♯m7 inversion is the trickiest physical challenge — students find it hard to
            squeeze the F♯ in. Jason suggests a slight wrist angle: push the wrist slightly to the
            left, but don't move the elbow much. And play from the wrist and fingers rather than
            the whole arm.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/12-with-a-little-help.png"
          caption="Jason's handwritten chord chart — intro, verse, chorus, bridge and outro. Only the verse is plain E major; the chorus and outro keep returning to D major, the ♭VII that gives them their Mixolydian colour."
        />

        <MTQuote>
          "So you could argue that more sections of the song are Mixolydian than they are major
          and yet probably the entire internet is saying it's on E major." — Jason on why this
          song is harder to explain than it looks.
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
